import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Server-powered infinite list with newest-wins request cancellation.
 *
 * `fetchPage({ offset, limit, reset, signal })` must return
 *   `{ rows, total, offset }` where `offset` is the ACTUAL start offset the
 *   server used (A-Z letter jumps compute their own), defaulting to the
 *   requested one.
 *
 * The list RESETS (aborting any in-flight request) whenever `depsKey`
 * changes, so callers pass an already-debounced key. Each fetch aborts the
 * previous one — a stale response can never overwrite a newer query, which
 * is exactly what fixes the "type a name → snaps back to A" race.
 */
export default function useServerSearchList({
  fetchPage,
  depsKey,
  pageSize = 100,
  enabled = true,
}) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [base, setBase] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const metaRef = useRef({ base: 0, len: 0, total: 0 });
  const moreInFlight = useRef(false);

  useEffect(() => {
    metaRef.current = { base, len: rows.length, total };
  }, [base, rows.length, total]);

  const isCancel = (e, ctrl) =>
    ctrl.signal.aborted || e?.name === "CanceledError" || e?.name === "AbortError";

  const reset = useCallback(async () => {
    abortRef.current?.abort();
    if (!enabled) {
      setRows([]);
      setTotal(0);
      setBase(0);
      setLoading(false);
      return;
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchRef.current({
        offset: 0,
        limit: pageSize,
        reset: true,
        signal: ctrl.signal,
      });
      if (ctrl.signal.aborted) return;
      setBase(res.offset || 0);
      setRows(res.rows || []);
      setTotal(res.total || 0);
    } catch (e) {
      if (isCancel(e, ctrl)) return;
      setError(e);
      setRows([]);
      setTotal(0);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [enabled, pageSize]);

  // Re-run on any dependency change. depsKey is a stable string the caller
  // recomputes when search/sort/filter/letter changes.
  useEffect(() => {
    reset();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  const loadMore = useCallback(async () => {
    const meta = metaRef.current;
    const nextOffset = meta.base + meta.len;
    if (moreInFlight.current || nextOffset >= meta.total || !enabled) return;
    moreInFlight.current = true;
    setLoadingMore(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetchRef.current({
        offset: nextOffset,
        limit: pageSize,
        reset: false,
        signal: ctrl.signal,
      });
      if (ctrl.signal.aborted) return;
      setRows((cur) => [...cur, ...(res.rows || [])]);
      if (typeof res.total === "number") setTotal(res.total);
    } catch (e) {
      if (isCancel(e, ctrl)) return;
    } finally {
      moreInFlight.current = false;
      if (!ctrl.signal.aborted) setLoadingMore(false);
    }
  }, [enabled, pageSize]);

  const hasMore = base + rows.length < total;

  return {
    rows,
    setRows,
    total,
    base,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    reload: reset,
    error,
  };
}
